/**
 * Progreso — MOBILE: lista de ejercicios y, al tocar uno, su detalle. La advertencia del e1RM de
 * §5.1 aparece junto al número, no escondida en un pie de página: es la que evita que alguien
 * compare su e1RM de leg press con el de otra persona.
 *
 * Cuál está abierto lo decide `useProgreso`, que lee el id de la URL: `/progreso/{ejercicio}` es
 * el contrato con el que "Ver su progresión" llega desde el registro.
 */

import type { ReactNode } from 'react';
import { formatearNumero, nombreMusculo, type Balance } from '@mg/core';

import {
  MIN_SEMANAS_GRAFICO,
  nombreSemana,
  placasDeBalance,
  semanasEnTexto,
  useProgreso,
} from '../features/useProgreso.ts';
import { Barras } from '../ui/charts.tsx';
import { Aviso, Cifra, Insignia, Pila, Vacio } from '../ui/componentes.tsx';

export function ProgresoMobile(): ReactNode {
  const p = useProgreso();

  if (p.datos.cargando) return <Vacio>Cargando…</Vacio>;
  if (p.filas.length === 0) return <Vacio>Todavía no hay series registradas.</Vacio>;

  const detalle = p.abierto ? p.detalle : null;

  if (detalle) {
    return (
      <>
        <button type="button" className="boton boton--fantasma boton--chico" onClick={() => p.abrir(null)}>
          ← Todos los ejercicios
        </button>

        <section className="tarjeta">
          <h2 className="titulo">{detalle.ejercicio.nombre}</h2>
          <p className="nota--fina">
            {nombreMusculo(detalle.ejercicio.musculo_primario)}
            {detalle.ejercicio.musculos_secundarios.length > 0 &&
              ` · también ${detalle.ejercicio.musculos_secundarios.map(nombreMusculo).join(', ')}`}
          </p>

          {detalle.estancamiento && (
            <div style={{ marginTop: 'var(--e3)' }}>
              <Aviso tono="atencion" titulo={`${detalle.estancamiento.sesiones} sesiones iguales`}>
                Mismo peso y mismas reps desde el {detalle.estancamiento.desde}. Probá una serie más
                o sumar reps antes de tocar la placa.
              </Aviso>
            </div>
          )}

          <div className="fila fila--entre" style={{ marginTop: 'var(--e4)' }}>
            <div>
              <span className="eyebrow">Peso máximo</span>
              <span className="cifra-grande">
                {detalle.records.pesoMax ? formatearNumero(detalle.records.pesoMax.valor) : '—'}
                <span className="unidad"> {detalle.records.pesoMax?.unidad ?? ''}</span>
              </span>
              <p className="nota--fina">{detalle.records.pesoMax?.fecha ?? ''}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span className="eyebrow">e1RM ({p.formula})</span>
              <Cifra valor={detalle.records.e1rmMax?.valor ?? null} unidad="kg" />
              {detalle.deltaE1rm !== null && (
                <p className="nota--fina" style={{ color: detalle.deltaE1rm > 0 ? 'var(--agua)' : undefined }}>
                  {detalle.deltaE1rm > 0 ? '+' : ''}
                  {formatearNumero(detalle.deltaE1rm)} kg
                </p>
              )}
            </div>
          </div>

          {detalle.ejercicio.tipo === 'maquina' && (
            <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
              {p.advertenciaE1rm}
            </p>
          )}
        </section>

        <section className="tarjeta">
          <span className="eyebrow">Volumen por semana</span>
          {/* Con una o dos semanas el gráfico es un bloque gigante que no dice nada más que la
              cifra. Se dice la cifra. */}
          {detalle.progresion.length < MIN_SEMANAS_GRAFICO ? (
            <div className="columna" style={{ gap: 4, marginTop: 'var(--e2)' }}>
              {semanasEnTexto(detalle.progresion).map((linea) => (
                <span key={linea} className="lectura">
                  {linea}
                </span>
              ))}
              <span className="nota--fina">
                {detalle.progresion.length === 1
                  ? 'Con una sola semana todavía no hay tendencia que dibujar.'
                  : 'Con dos semanas todavía no hay tendencia que dibujar.'}
              </span>
            </div>
          ) : (
            <Barras
              datos={detalle.progresion.map((s) => ({ etiqueta: s.semana.slice(6), valor: s.volumenKg }))}
              alto={140}
              unidad="kg"
            />
          )}
        </section>

        <section className="tarjeta">
          <span className="eyebrow">Semanas</span>
          <div className="columna" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)' }}>
            {[...detalle.progresion].reverse().map((s) => (
              <div key={s.semana} className="fila fila--entre">
                <span className="lectura nota--fina">{s.semana}</span>
                <span className="lectura nota--fina">
                  {formatearNumero(s.pesoMaxKg)} kg · {s.series} series · {s.repsTotales} reps
                </span>
              </div>
            ))}
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {p.pedidoSinDatos && (
        <Aviso tono="info">
          Todavía no hay series registradas de {p.pedidoSinDatos.nombre}: la primera que anotes lo
          abre acá.
        </Aviso>
      )}

      <section className="tarjeta">
        <span className="eyebrow">Balance · todo el historial</span>
        <div className="columna" style={{ gap: 'var(--e4)', marginTop: 'var(--e3)' }}>
          <BarraBalance
            titulo="Empuje vs. tirón"
            izq="empuje"
            der="tirón"
            balance={p.balances.empujeTiron}
          />
          <BarraBalance
            titulo="Superior vs. pierna"
            izq="superior"
            der="pierna"
            balance={p.balances.superiorInferior}
          />
        </div>
        {/* Sin esto, "11 empuje · 9 tirón" (20) arriba de "23 superior · 0 pierna" (23) se lee
            como un error de la app. */}
        {p.notaBalances && (
          <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
            {p.notaBalances}
          </p>
        )}
      </section>

      <section className="tarjeta">
        <span className="eyebrow">Frecuencia · veces por semana (28 d)</span>
        <div className="columna" style={{ gap: 6, marginTop: 'var(--e3)' }}>
          {p.frecuencia.slice(0, 8).map((f) => (
            <div key={f.musculo} className="fila fila--entre">
              <span style={{ fontSize: 13 }}>{nombreMusculo(f.musculo)}</span>
              <span className="lectura nota--fina">{formatearNumero(f.veces, 1)}×</span>
            </div>
          ))}
        </div>
        {p.lecturaFrecuencia && (
          <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
            {p.lecturaFrecuencia}
          </p>
        )}
      </section>

      {p.cardio.porNivel.length > 0 && (
        <section className="tarjeta">
          <span className="eyebrow">Cardio · a igual nivel</span>
          <div className="columna" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
            {p.cardio.porNivel.map((n) => (
              <div key={n.nivel} className="fila fila--entre">
                <span>
                  Nivel <span className="lectura">{n.nivel}</span>
                  <span className="apagado nota--fina"> · {n.sesiones.length} sesiones</span>
                </span>
                <span className="lectura nota--fina" style={{ color: n.mejora ? 'var(--agua)' : undefined }}>
                  {n.deltaDistanciaKm === null
                    ? 'sin comparación'
                    : `${n.deltaDistanciaKm > 0 ? '+' : ''}${formatearNumero(n.deltaDistanciaKm, 2)} km`}
                </span>
              </div>
            ))}
          </div>
          {p.cardio.sinNivel > 0 && (
            <p className="nota--fina" style={{ marginTop: 'var(--e3)', color: 'var(--ambar)' }}>
              {p.cardio.sinNivel} {p.cardio.sinNivel === 1 ? 'sesión' : 'sesiones'} sin nivel de
              resistencia: no entran en ninguna tendencia.
            </p>
          )}
        </section>
      )}

      <span className="eyebrow">Ejercicios</span>
      {p.filas.map((f) => (
        <button
          key={f.ejercicio.id}
          type="button"
          className="tarjeta"
          onClick={() => p.abrir(f.ejercicio.id)}
          style={{ textAlign: 'left', color: 'inherit', cursor: 'pointer' }}
        >
          <div className="fila fila--entre">
            <div>
              <div className="fila" style={{ gap: 6 }}>
                <strong>{f.ejercicio.nombre}</strong>
                {f.estancamiento && <Insignia tono="ambar">estancado</Insignia>}
              </div>
              <p className="nota--fina" style={{ marginTop: 2 }}>
                {f.sesiones} {f.sesiones === 1 ? 'sesión' : 'sesiones'} · última {f.ultimaFecha}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span className="lectura">
                {f.records.pesoMax ? `${formatearNumero(f.records.pesoMax.valor)} ${f.records.pesoMax.unidad}` : '—'}
              </span>
              {f.deltaE1rm !== null && (
                <p className="nota--fina" style={{ color: f.deltaE1rm > 0 ? 'var(--agua)' : undefined }}>
                  {f.deltaE1rm > 0 ? '+' : ''}
                  {formatearNumero(f.deltaE1rm)} kg e1RM
                </p>
              )}
            </div>
          </div>
        </button>
      ))}
    </>
  );
}

function BarraBalance({
  titulo,
  izq,
  der,
  balance,
}: {
  titulo: string;
  izq: string;
  der: string;
  balance: Balance;
}): ReactNode {
  return (
    <div>
      {/* "9 / 9" se leía como "9 de 9 hechos". Cada número con lo que cuenta al lado. */}
      <div className="fila fila--entre" style={{ marginBottom: 4 }}>
        <span style={{ fontSize: 13 }}>{titulo}</span>
        <span className="nota--fina">
          <span className="lectura">{balance.a}</span> {izq} · <span className="lectura">{balance.b}</span> {der}
        </span>
      </div>
      {/* Placas enteras: media placa pintada se ve rota, no se lee como "y medio". */}
      <Pila
        estados={placasDeBalance(balance, 12, balance.desbalanceado ? 'ambar' : 'llena')}
        alto={12}
        etiqueta={`${balance.a} de ${izq} contra ${balance.b} de ${der}`}
      />
      <div className="fila fila--entre">
        <span className="nota--fina">{izq}</span>
        <span className="nota--fina">{der}</span>
      </div>
      {balance.desbalanceado && (
        <p className="nota--fina" style={{ color: 'var(--ambar)', marginTop: 2 }}>
          {balance.detalle}
        </p>
      )}
    </div>
  );
}
